import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { BaasI18n } from '@lvflanders/bar';

/**
 * Defines how the close dialog button should behave when clicked.
 *
 * - `'auto'`:
 *    Closes the dialog immediately using Angular Material's built-in
 *    `mat-dialog-close` mechanism. No lifecycle events are emitted.
 *
 * - `'autoTrigger'`:
 *    Closes the dialog automatically *and* emits the close output event.
 *    Use this when you want automatic closing but still need to run logic around it.
 *
 * - `'manual'`:
 *    Does not close the dialog. Instead it only emits the close
 *    output event, leaving the consumer in full control to close the dialog
 *    manually (e.g., after validation or async work).
 */
type BaasDialogCloseBehavior = 'auto' | 'manual' | 'autoTrigger';

@Component({
    selector: 'baas-close-dialog-button',
    templateUrl: './baas-close-dialog-button.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButtonModule, MatDialogModule, MatIcon, MatTooltip],
    host: {
        class: 'baas-close-dialog-button'
    }
})
/** Standalone component for a close button in Material dialogs with icon and tooltip. */
export class BaasCloseDialogButton {
    readonly #dialogRef = inject(MatDialogRef, { optional: true });
    readonly #i18n = inject(BaasI18n);
    /** The value to pass to mat-dialog-close on the internal button. */
    readonly closeValue = input<unknown>(undefined);
    /** The tooltip text. */
    readonly tooltip = input<string>(this.#i18n.get('DOMAIN_BAR_CONTROLS.CLOSE_DIALOG_BUTTON.CLOSE'));
    /** Determines how the close button behaves when pressed. */
    readonly closeBehavior = input<BaasDialogCloseBehavior>('auto');
    /** Emitted when the close button is clicked and before the dialog is closed. Consumers can use this instead of mat-dialog-close in case autoClose is set to false. */
    /** Emitted when the close button is clicked and after the dialog is closed. Consumers can use this instead of mat-dialog-close in case autoClose is set to false. */
    // eslint-disable-next-line @angular-eslint/no-output-native
    readonly close = output<void>();

    constructor() {
        if (!this.#dialogRef) {
            throw new Error(`${this.constructor.name} must be used within a MatDialog context.`);
        }
    }

    protected onClose(): void {
        this.close.emit();
        if (this.closeBehavior() == 'autoTrigger') this.#dialogRef.close(this.closeValue());
    }
}
